import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import type { LlmTool } from "@/types/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

export function LlmToolsListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation("tools");

  const { data, isLoading, error } = useQuery({
    queryKey: ["llm-tools"],
    queryFn: () => api.get<{ tools: LlmTool[] }>("/api/admin/llm-tools"),
  });

  const byCategory: Record<string, LlmTool[]> = {};
  for (const tool of data?.tools ?? []) {
    if (!byCategory[tool.category]) byCategory[tool.category] = [];
    byCategory[tool.category].push(tool);
  }
  const categories = Object.keys(byCategory).sort();

  const renameCategory = useMutation({
    mutationFn: async ({ from, to }: { from: string; to: string }) => {
      const tools = (data?.tools ?? []).filter((tool) => tool.category === from);
      // Sequential to keep error reporting simple; tool count is small.
      for (const tool of tools) {
        await api.put(`/api/admin/llm-tools/${tool.id}`, { category: to });
      }
      return tools.length;
    },
    onSuccess: (count, vars) => {
      toast.success(t("list.renamed", { from: vars.from, to: vars.to, count }));
      qc.invalidateQueries({ queryKey: ["llm-tools"] });
      qc.invalidateQueries({ queryKey: ["llm-tools-summaries"] });
    },
    onError: (err) =>
      toast.error(
        t("list.renameFailed", { message: err instanceof Error ? err.message : String(err) })
      ),
  });

  function handleRename(category: string) {
    const next = window.prompt(t("list.renamePrompt", { category }), category);
    if (!next || next.trim() === "" || next === category) return;
    renameCategory.mutate({ from: category, to: next.trim() });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("list.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("list.description")}
          </p>
        </div>
        <Button onClick={() => navigate("/tools/new")}>
          <Plus className="h-4 w-4" /> {t("list.newTool")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("list.allTools")}</CardTitle>
          <CardDescription>
            {isLoading
              ? t("list.loading")
              : t("list.summary", {
                  tools: data?.tools?.length ?? 0,
                  categories: categories.length,
                })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">
              {t("errors.loadFailed", {
                message: error instanceof Error ? error.message : String(error),
              })}
            </p>
          ) : !categories.length ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t("list.empty")}
            </p>
          ) : (
            categories.map((cat) => (
              <div key={cat}>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {cat}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRename(cat)}
                    disabled={renameCategory.isPending}
                  >
                    <Pencil className="h-3 w-3" />
                    <span>{t("list.renameCategory")}</span>
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("list.columns.tool")}</TableHead>
                      <TableHead>{t("list.columns.description")}</TableHead>
                      <TableHead>{t("list.columns.status")}</TableHead>
                      <TableHead className="w-[60px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byCategory[cat].map((tool) => (
                      <TableRow
                        key={tool.id}
                        className="cursor-pointer"
                        onClick={() => navigate(`/tools/${tool.id}`)}
                      >
                        <TableCell>
                          <div className="font-medium font-mono text-xs">{tool.toolId}</div>
                          {tool.name !== tool.toolId && (
                            <div className="text-xs text-muted-foreground">{tool.name}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{tool.description || "—"}</TableCell>
                        <TableCell>
                          <Badge variant={tool.isActive ? "success" : "muted"}>
                            {tool.isActive ? t("status.active") : t("status.inactive")}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Link
                            to={`/tools/${tool.id}`}
                            className="text-xs text-muted-foreground hover:text-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t("list.edit")}
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <CategorySearchHint />
    </div>
  );
}

function CategorySearchHint() {
  // Tiny hint card — feels less empty than just a single Card.
  const { t } = useTranslation("tools");
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("list.hint.title")}</CardTitle>
        <CardDescription>
          {t("list.hint.description")}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}

// Re-exported for use by the form page.
export function useToolCategories(): string[] {
  const { data } = useQuery({
    queryKey: ["llm-tools"],
    queryFn: () => api.get<{ tools: LlmTool[] }>("/api/admin/llm-tools"),
  });
  return Array.from(new Set((data?.tools ?? []).map((tool) => tool.category))).sort();
}
